import { useCallback, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { resetPasswordExperienceViewController } from '@aqx-core/common/services/reset-password-experience';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import { getEnterValidText, isValidEmail } from '@aqx-core/common/validators';

export interface IResetPasswordFormValues {
  email: string;
}

const EMAIL_VALIDATION_TEXT = getEnterValidText({ label: 'email' });

const schema = yup.object().shape({
  email: yup
    .string()
    .required(EMAIL_VALIDATION_TEXT)
    .test('is-valid-email', EMAIL_VALIDATION_TEXT, (value) => (value ? isValidEmail(value) : false))
});

export const useResetPasswordForm = () => {
  const useFormMethods = useBaseForm<IResetPasswordFormValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    shouldFocusError: false
  });

  const { handleSubmit, trigger, watch, formState } = useFormMethods;

  const [email] = watch(['email']);

  const {
    errors: { email: emailError }
  } = formState;

  const isDisabled = useMemo(() => {
    return !!(!email || emailError);
  }, [email, emailError]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<IResetPasswordFormValues> = useCallback(
    (values) => {
      resetPasswordExperienceViewController.resetPassword({ params: values, onError });
    },
    [onError]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  return {
    useFormMethods,
    submit,
    trigger,
    isDisabled,
    email
  };
};
